import React, { useState } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  Wallet,
  Building2,
  Truck,
  ScrollText,
  Layers,
  Coins,
  RotateCw,
  TrendingUp,
  ShieldAlert,
  Leaf,
  Scale,
  FileSignature,
} from "lucide-react";
import { PageHeader, KpiCard, SectionCard, RagBadge } from "@/components/Shared";
import {
  assetRegister,
  assetTotals,
  costStructure,
  costStructureTotals,
  restartModel,
  cashflow,
  valueCreationLevers,
  valueCreationTotals,
  liabilities,
  costPerTon,
} from "@/data/financialData";
import { rehabilitation } from "@/data/geoEsgData";

const tooltipStyle = {
  contentStyle: { backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 },
};

const conditionStyles = {
  Excellent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Good: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Average: "bg-amber-50 text-amber-700 border-amber-200",
  Poor: "bg-red-50 text-red-700 border-red-200",
  "—": "bg-slate-100 text-slate-700 border-slate-200",
};

const classIcons = {
  "Fixed Infrastructure": Building2,
  "Mobile Equipment": Truck,
  "Licences & Rights": ScrollText,
  Stockpiles: Layers,
};

function fmtR(m) {
  if (m >= 1000) return `R${(m / 1000).toFixed(2)}bn`;
  return `R${Math.round(m).toLocaleString()}m`;
}

export default function FinancialIntelligence() {
  const [assetClass, setAssetClass] = useState("All");
  const classes = ["All", ...Array.from(new Set(assetRegister.map((a) => a.class)))];
  const filteredAssets = assetClass === "All" ? assetRegister : assetRegister.filter((a) => a.class === assetClass);

  return (
    <div data-testid="page-financial-intelligence">
      <PageHeader
        eyebrow="Financial & Value Intelligence"
        title="What is the mine worth, what does it cost, and what can it become?"
        subtitle="Asset valuation, cost structure, cashflow, value creation and risk — connected into a single decision-support view."
      />

      {/* Top-line KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="fi-asset-value" label="Total Asset Value" value={fmtR(assetTotals.totalBookValue)} unit="book value · ZAR" />
        <KpiCard testid="fi-replacement" label="Replacement Cost" value={fmtR(assetTotals.totalReplacement)} unit="if rebuilt today" accent="amber" />
        <KpiCard testid="fi-daily-cost" label="Daily OpEx" value={`R${costStructureTotals.daily.toFixed(1)}m`} unit="cash burn · all-in" />
        <KpiCard testid="fi-fcf" label="Free Cashflow" value={fmtR(cashflow.freeCashflow)} unit={`payback ${cashflow.paybackYears.toFixed(1)} yrs`} accent="green" />
      </div>

      {/* 1. ASSET REGISTER & VALUATION */}
      <SectionCard
        title="Asset Register & Valuation"
        description={`${assetRegister.length} key assets across infrastructure, mobile fleet, licences and stockpiles`}
        action={<Wallet size={16} className="text-amber-700" />}
        className="mb-6"
      >
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {classes.map((c) => {
            const Icon = classIcons[c] || Layers;
            return (
              <button
                key={c}
                onClick={() => setAssetClass(c)}
                data-testid={`asset-class-${c.split(" ")[0].toLowerCase()}`}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  assetClass === c
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                }`}
              >
                {c !== "All" && <Icon size={12} />} {c}
              </button>
            );
          })}
        </div>

        <div className="overflow-x-auto" data-testid="asset-register">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-3 pr-4 font-semibold">Asset</th>
                <th className="py-3 pr-4 font-semibold">Class</th>
                <th className="py-3 pr-4 font-semibold w-16">Age</th>
                <th className="py-3 pr-4 font-semibold">Condition</th>
                <th className="py-3 pr-4 font-semibold text-right">Book Value</th>
                <th className="py-3 pr-4 font-semibold text-right">Depreciation</th>
                <th className="py-3 pr-4 font-semibold text-right">Replacement</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssets.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`asset-${a.id}`}>
                  <td className="py-3 pr-4 font-medium text-slate-900">{a.name}</td>
                  <td className="py-3 pr-4 text-slate-600 text-xs">{a.class}</td>
                  <td className="py-3 pr-4 text-slate-700 text-xs font-mono">{a.age || "—"} {a.age ? "yrs" : ""}</td>
                  <td className="py-3 pr-4">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${conditionStyles[a.condition] || conditionStyles["—"]}`}>
                      {a.condition}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-right font-display font-semibold text-slate-900">R{a.bookValue.toLocaleString()}m</td>
                  <td className="py-3 pr-4 text-right text-slate-600">R{a.depreciation.toLocaleString()}m</td>
                  <td className="py-3 pr-4 text-right text-amber-700 font-semibold">R{a.replacementValue.toLocaleString()}m</td>
                </tr>
              ))}
              <tr className="bg-slate-50">
                <td className="py-3 pr-4 font-bold text-slate-900" colSpan={4}>Total ({filteredAssets.length} assets)</td>
                <td className="py-3 pr-4 text-right font-display font-bold text-slate-900">
                  R{filteredAssets.reduce((s, a) => s + a.bookValue, 0).toLocaleString()}m
                </td>
                <td className="py-3 pr-4 text-right font-semibold text-slate-700">
                  R{filteredAssets.reduce((s, a) => s + a.depreciation, 0).toLocaleString()}m
                </td>
                <td className="py-3 pr-4 text-right font-display font-bold text-amber-700">
                  R{filteredAssets.reduce((s, a) => s + a.replacementValue, 0).toLocaleString()}m
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* 2. COST STRUCTURE */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <SectionCard
          title="Cost Structure — Daily"
          description="Cash burn rate by line item · 24-hr operating profile"
          action={<Coins size={16} className="text-amber-700" />}
          className="xl:col-span-2"
        >
          <div className="h-72" data-testid="cost-structure-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={costStructure} margin={{ top: 8, right: 8, left: -8, bottom: 0 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `R${v}m`} />
                <YAxis type="category" dataKey="line" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={130} />
                <Tooltip {...tooltipStyle} formatter={(v) => `R${v.toFixed(2)}m / day`} />
                <Bar dataKey="dailyRm" radius={[0, 4, 4, 0]}>
                  {costStructure.map((c) => <Cell key={c.line} fill={c.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Cost Structure — Monthly" description="ZAR millions per month">
          <div className="space-y-2.5" data-testid="cost-structure-monthly">
            {costStructure.map((c) => (
              <div key={c.line}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-700">{c.line}</span>
                  <span className="font-display font-semibold text-slate-900">R{c.monthlyRm.toFixed(0)}m</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full" style={{ width: `${c.share * 2.5}%`, background: c.color }} />
                </div>
              </div>
            ))}
            <div className="pt-3 mt-3 border-t border-slate-200 flex items-center justify-between">
              <span className="font-semibold text-slate-900 text-sm">Total monthly</span>
              <span className="font-display text-lg font-bold text-amber-700">R{Math.round(costStructureTotals.monthly).toLocaleString()}m</span>
            </div>
            <div className="text-[11px] text-slate-500">
              ≈ R{costStructureTotals.daily.toFixed(2)}m / day · R{(costStructureTotals.annual / 1000).toFixed(2)}bn / year
            </div>
          </div>
        </SectionCard>
      </div>

      {/* 3. REACTIVATION / START-UP COST MODEL */}
      <SectionCard
        title="Reactivation / Start-up Cost Model"
        description={restartModel.scenario}
        action={<RotateCw size={16} className="text-amber-700" />}
        className="mb-6"
      >
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 mb-4">
          <strong>Note:</strong> {restartModel.status}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="restart-model">
          {restartModel.lineItems.map((li, i) => (
            <div key={li.item} data-testid={`restart-item-${i}`} className="bg-white border border-slate-200 rounded-md p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{li.item}</div>
              <div className="font-display text-2xl font-bold text-slate-900 mt-1">R{li.cost}m</div>
              <p className="text-[11px] text-slate-600 mt-2 leading-relaxed">{li.note}</p>
            </div>
          ))}
        </div>

        <div className="bg-slate-900 rounded-md p-4 text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-amber-400 font-semibold">Total restart cost</div>
            <div className="font-display text-3xl font-bold mt-1" data-testid="restart-total">
              R{restartModel.lineItems.reduce((s, l) => s + l.cost, 0)}m
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-amber-400 font-semibold">Time to restart</div>
            <div className="font-display text-3xl font-bold mt-1">{restartModel.timelineDays} days</div>
          </div>
        </div>
      </SectionCard>

      {/* 4. CASHFLOW VIEW */}
      <SectionCard
        title="Cashflow View"
        description="Simplified annual financial model — ZAR millions"
        className="mb-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="cashflow-view">
          <CashRow label="Annual Revenue" value={fmtR(cashflow.revenue)} sub="Top-line, all commodities" tone="neutral" />
          <CashRow label="Operating Cost" value={`(${fmtR(cashflow.opex)})`} sub="OpEx incl. labour, energy, maintenance" tone="negative" />
          <CashRow label="EBITDA" value={fmtR(cashflow.ebitda)} sub={`${cashflow.ebitdaMargin.toFixed(1)}% margin`} tone="positive" highlight />
          <CashRow label="Capex" value={`(${fmtR(cashflow.capex)})`} sub="Sustaining + small projects" tone="negative" />
          <CashRow label="Free Cashflow" value={fmtR(cashflow.freeCashflow)} sub="EBITDA − Capex" tone="positive" highlight />
          <CashRow label="Payback Period" value={`${cashflow.paybackYears.toFixed(1)} yrs`} sub={`vs ${fmtR(cashflow.askingPrice)} asking`} tone="neutral" />
        </div>
      </SectionCard>

      {/* Closure & ESG provisions (cross-link to Geological & ESG Intelligence) */}
      <SectionCard
        title="Closure & ESG Provisions"
        description="Off-balance commitments tracked under Geological & ESG Intelligence"
        action={<Leaf size={16} className="text-emerald-700" />}
        className="mb-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="fi-closure-provisions">
          <ProvisionTile label="Closure liability provision" value={`R${rehabilitation.liabilityProvisionRm}m`} sub="Standard Bank escrow" tone="slate" />
          <ProvisionTile label="Rehabilitation guarantee" value={`R${rehabilitation.rehabGuaranteeRm}m`} sub="In favour of DMRE" tone="emerald" />
          <ProvisionTile label="Water-quality provision" value={`R${rehabilitation.waterComplianceProvisionRm}m`} sub="Pending DWS Q2 audit" tone="amber" />
          <ProvisionTile label="Total ESG / closure cover" value={`R${rehabilitation.liabilityProvisionRm + rehabilitation.rehabGuaranteeRm + rehabilitation.waterComplianceProvisionRm}m`} sub={`${rehabilitation.closureReadinessPct}% closure readiness`} tone="emphasis" />
        </div>
        <div className="mt-3 text-[11px] text-slate-500">
          Detailed rehabilitation register, DMRE compliance and SLP commitments live in{" "}
          <a href="/geo-esg" className="text-amber-700 font-semibold hover:underline" data-testid="fi-link-geo-esg">Geological & ESG Intelligence</a>.
        </div>
      </SectionCard>

      {/* 5. VALUE CREATION MODEL */}
      <SectionCard
        title="Value Creation — Before vs After"
        description="Operational improvements translated into monthly cashflow uplift"
        action={<TrendingUp size={16} className="text-emerald-700" />}
        className="mb-6"
      >
        <div className="overflow-x-auto" data-testid="value-creation-model">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-3 pr-4 font-semibold">Lever</th>
                <th className="py-3 pr-4 font-semibold">Driver</th>
                <th className="py-3 pr-4 font-semibold text-right">Before</th>
                <th className="py-3 pr-4 font-semibold text-right">After</th>
                <th className="py-3 pr-4 font-semibold text-right w-32">Monthly impact</th>
              </tr>
            </thead>
            <tbody>
              {valueCreationLevers.map((l) => (
                <tr key={l.lever} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="py-3 pr-4 font-medium text-slate-900">{l.lever}</td>
                  <td className="py-3 pr-4 text-slate-600 text-xs">{l.basis}</td>
                  <td className="py-3 pr-4 text-right text-slate-500 font-mono">R{l.before.toFixed(1)}m</td>
                  <td className="py-3 pr-4 text-right font-semibold text-emerald-700 font-mono">R{l.after.toFixed(1)}m</td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2 justify-end">
                      <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-600" style={{ width: `${(l.after / 35) * 100}%` }} />
                      </div>
                      <span className="text-xs text-emerald-700 font-bold w-14 text-right">+R{l.after.toFixed(1)}m</span>
                    </div>
                  </td>
                </tr>
              ))}
              <tr className="bg-emerald-50">
                <td className="py-3 pr-4 font-bold text-slate-900" colSpan={3}>Total uplift</td>
                <td className="py-3 pr-4 text-right font-display font-bold text-emerald-700">R{valueCreationTotals.monthlyUplift.toFixed(1)}m / month</td>
                <td className="py-3 pr-4 text-right font-display font-bold text-emerald-700" data-testid="value-creation-annual">
                  R{Math.round(valueCreationTotals.annualUplift)}m / year
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Cost per ton mini-card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Cost per Ton — Current</div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-2">R{costPerTon.current.toLocaleString()}</div>
          <div className="text-xs text-slate-500 mt-1">vs industry median R{costPerTon.industryMedian.toLocaleString()}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Cost per Ton — Target</div>
          <div className="font-display text-3xl font-bold text-amber-700 mt-2">R{costPerTon.target.toLocaleString()}</div>
          <div className="text-xs text-slate-500 mt-1">{costPerTon.note}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Per-ton Saving</div>
          <div className="font-display text-3xl font-bold text-emerald-700 mt-2">{costPerTon.delta} / t</div>
          <div className="text-xs text-emerald-700/80 mt-1">≈ R260m/yr at 1.24M t throughput</div>
        </div>
      </div>

      {/* 6. RISK & LIABILITY PANEL */}
      <SectionCard
        title="Risk & Liability Panel"
        description="Environmental, regulatory and contractual exposures"
        action={<ShieldAlert size={16} className="text-red-600" />}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="risk-liability">
          <LiabilityCol icon={Leaf} title="Environmental" items={liabilities.environmental} accent="emerald" />
          <LiabilityCol icon={Scale} title="Regulatory" items={liabilities.regulatory} accent="amber" />
          <LiabilityCol icon={FileSignature} title="Contractual" items={liabilities.contractual} accent="blue" />
        </div>
      </SectionCard>
    </div>
  );
}

function CashRow({ label, value, sub, tone, highlight }) {
  const toneCls = tone === "positive" ? "text-emerald-700" : tone === "negative" ? "text-red-600" : "text-slate-900";
  return (
    <div className={`p-4 rounded-md border ${highlight ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200"}`}>
      <div className={`text-[10px] uppercase tracking-wider font-semibold ${highlight ? "text-amber-400" : "text-slate-500"}`}>{label}</div>
      <div className={`font-display text-2xl md:text-3xl font-bold mt-1 ${highlight ? "text-amber-400" : toneCls}`}>{value}</div>
      <div className={`text-xs mt-1 ${highlight ? "text-slate-300" : "text-slate-500"}`}>{sub}</div>
    </div>
  );
}

function LiabilityCol({ icon: Icon, title, items, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className={`text-${accent}-700`} />
        <h4 className="font-display text-sm font-semibold text-slate-900">{title}</h4>
      </div>
      <ul className="space-y-3">
        {items.map((it) => (
          <li key={it.item} className="border-l-2 pl-3 py-1" style={{ borderColor: it.status === "green" ? "#059669" : it.status === "amber" ? "#D97706" : "#DC2626" }}>
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <span className="text-xs font-semibold text-slate-900 truncate">{it.item}</span>
              <RagBadge level={it.status} />
            </div>
            <div className="text-[11px] text-slate-700 font-mono">{it.value}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{it.detail}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}


function ProvisionTile({ label, value, sub, tone }) {
  const toneClass =
    tone === "emphasis" ? "bg-slate-900 text-white border-slate-900" :
    tone === "emerald" ? "bg-emerald-50 border-emerald-200" :
    tone === "amber" ? "bg-amber-50 border-amber-200" :
    "bg-white border-slate-200";
  const labelClass = tone === "emphasis" ? "text-amber-400" : "text-slate-500";
  const valueClass = tone === "emphasis" ? "text-amber-400" : tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-slate-900";
  const subClass = tone === "emphasis" ? "text-slate-300" : "text-slate-500";
  return (
    <div className={`border rounded-md p-4 ${toneClass}`}>
      <div className={`text-[10px] uppercase tracking-wider font-semibold ${labelClass}`}>{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${valueClass}`}>{value}</div>
      <div className={`text-xs mt-1 ${subClass}`}>{sub}</div>
    </div>
  );
}
